import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import useFormHook from "@shared/hooks/useFormHook";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Select from "react-select";
import { signeesService } from "@modules/organizationalStructure/signees/services/signees";
import { companiesService } from "@shared/services/companiesService";
import { useDebounce } from "@shared/hooks/useDebounce";
import LoadingButton from "@shared/components/LoadingButton";

function normalizeCompany(company, fallbackId) {
    if (!company) {
        return null;
    }

    const id = company.id ?? fallbackId;
    const code = company.code || company.branchNo || company.branchCode || '';
    const name = company.name || company.branchName || company.longDescription || '';

    return {
        ...company,
        id,
        code,
        name,
    };
}

function getCompanyLabel(company) {
    if (!company) {
        return '';
    }

    const code = company.code || company.branchNo || '';
    const name = company.name || company.branchName || '';

    if (code && name) {
        return `${code} - ${name}`;
    }

    return name || code || String(company.id ?? '');
}

export default function SigneesForm({ onClose, editData, handleConfirm }) {
    const { t } = useTranslation();
    const { modalRef } = useFormHook({}, onClose);
    const adminBranchId = useSelector((state) => state.auth.adminBranchId);
    const [companies, setCompanies] = useState([]);
    const [persons, setPersons] = useState([]);
    const [selectedPerson, setSelectedPerson] = useState(null);
    const [isLoadingPersons, setIsLoadingPersons] = useState(false);
    const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
    const [loading, setLoading] = useState(false);
    const validationSchema = yup.object().shape({
        personId: yup.string().required(t('common.required')),
        company: yup.string().required(t('common.required')),
        position: yup.string().required(t('common.required')),
    });
    const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm({
        resolver: yupResolver(validationSchema)
    });
    const watchedValues = watch();

    const customSelectStyles = (hasError) => ({
        control: (base, state) => ({
            ...base,
            minHeight: '24px',
            height: '24px',
            fontSize: '14px',
            padding: '0',
            borderColor: hasError ? 'red' : (state.isFocused ? '#2684ff' : base.borderColor),
            borderWidth: hasError ? '1px' : base.borderWidth,
            boxShadow: hasError ? '0 0 0 1px red' : (state.isFocused ? base.boxShadow : 'none'),
            '&:hover': {
                borderColor: hasError ? 'red' : (state.isFocused ? '#2684ff' : '#b3b3b3'),
            },
        }),
        valueContainer: (base) => ({
            ...base,
            height: '24px',
            padding: '0 4px',
        }),
        input: (base) => ({
            ...base,
            margin: '0',
            padding: '0',
        }),
        indicatorsContainer: (base) => ({
            ...base,
            height: '24px',
        }),
        dropdownIndicator: (base) => ({
            ...base,
            padding: '0 4px',
        }),
        clearIndicator: (base) => ({
            ...base,
            padding: '0 4px',
        }),
    });

    useEffect(() => {
        const applyCompany = (company) => {
            const normalizedCompany = normalizeCompany(company, adminBranchId);
            if (!normalizedCompany) {
                setCompanies([]);
                return;
            }

            setCompanies([normalizedCompany]);

            if (!editData) {
                setValue('company', String(normalizedCompany.id));
            }
        };

        const fetchCompany = async () => {
            if (adminBranchId === null || adminBranchId === undefined) {
                setCompanies([]);
                setIsLoadingCompanies(false);
                return;
            }

            try {
                setIsLoadingCompanies(true);
                const company = await companiesService.getSelectedCompany(adminBranchId);
                applyCompany(company);
            } catch (error) {
                console.error('Error fetching company:', error);
                setCompanies([]);
            } finally {
                setIsLoadingCompanies(false);
            }
        };

        fetchCompany();
    }, [adminBranchId, editData, setValue]);

    useEffect(() => {
        if (!editData) {
            return;
        }

        setValue('personId', editData.personId || '');
        setValue('position', editData.jobDescription || '');
        setValue('firstSignatures', editData.docSign1 || false);
        setValue('secondSignatures', editData.docSign2 || false);
        setValue('status', editData.active ? '1' : '2');

        if (editData.personId) {
            const employeeName = `${editData.personName || ''} ${editData.personFatherName || ''} ${editData.personSurName || ''}`.trim();
            const personOption = {
                value: editData.personId,
                label: employeeName
            };
            setSelectedPerson(personOption);
        }
    }, [editData, setValue]);

    useEffect(() => {
        if (!editData || companies.length === 0) {
            return;
        }

        setValue('company', editData.empBranchId != null ? String(editData.empBranchId) : '');
    }, [editData, companies, setValue]);

    const searchPersons = async (searchInput) => {
        if (!searchInput || searchInput.length < 2) {
            setPersons([]);
            return;
        }

        try {
            setIsLoadingPersons(true);

            const searchTerms = searchInput.trim().split(/\s+/);
            let searchParams = {};

            if (searchTerms.length === 1) {
                searchParams.name = searchTerms[0];
            } else if (searchTerms.length === 2) {
                searchParams.name = searchTerms[0];
                searchParams.fathersName = searchTerms[1];
            } else if (searchTerms.length >= 3) {
                searchParams.name = searchTerms[0];
                searchParams.fathersName = searchTerms[1];
                searchParams.surname = searchTerms[2];
            }

            const response = await signeesService.searchPersons(searchParams);


            const personOptions = (response.content || []).map(person => ({
                value: person.id,
                label: `${person.name || ''} ${person.fathersName || ''} ${person.surname || ''}`.trim()
            })) || [];

            setPersons(personOptions);
        } catch (error) {
            console.error('Error searching persons:', error);
            setPersons([]);
        } finally {
            setIsLoadingPersons(false);
        }
    };

    const debouncedSearchPersons = useDebounce(searchPersons, 1000);

    const handlePersonSelection = (selectedOption) => {
        setSelectedPerson(selectedOption);
        if (selectedOption) {
            setValue('personId', selectedOption.value);
            setValue('employeeName', selectedOption.label);
        } else {
            setValue('personId', '');
            setValue('employeeName', '');
        }
    };

    const submitForm = async (data) => {
        setLoading(true);
        try {
            const transformedData = {
                personId: parseInt(data.personId),
                empBranchId: parseInt(data.company),
                jobDescription: data.position,
                docSign1: data.firstSignatures,
                docSign2: data.secondSignatures,
                active: data.status === "1"
            };

            if (handleConfirm) {
                await handleConfirm(transformedData);
            }
        } catch (error) {
            console.error('Error submitting form:', error);
        } finally {
            setLoading(false);
        }
    }

    const handleClose = () => {
        onClose();
    }

    return (
        <div className="modal show" tabIndex="-1">
            <div className="modal-dialog modal-confirm" ref={modalRef}>
                <div className="modal-content">
                    <button type="button" className="close" onClick={handleClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                    <div className="modal-body">
                        <h4 className="modal-title">
                            {editData ? t('common.edit') : t('common.add')} {t('organizationalStructure.signees.title')}
                        </h4>
                        <form id="signeesForm" className="region-form" onSubmit={handleSubmit(submitForm)}>
                            <div className="flex-column">
                                <div className="form-section width-90">
                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.signees.form.employeeName')}<span className="required">*</span></span>
                                        <Select
                                            styles={customSelectStyles}
                                            name='personId'
                                            placeholder={t('users.table.search.startType')}
                                            options={persons}
                                            value={selectedPerson}
                                            onChange={handlePersonSelection}
                                            onInputChange={(inputValue) => {
                                                if (inputValue && inputValue.length >= 2) {
                                                    debouncedSearchPersons(inputValue);
                                                } else {
                                                    setPersons([]);
                                                }
                                                return inputValue;
                                            }}
                                            isLoading={isLoadingPersons}
                                            isSearchable={true}
                                            isClearable={true}
                                            noOptionsMessage={() => t('common.select')}
                                            loadingMessage={() => t('common.loading')}
                                        />
                                        {errors.personId && <p className="error-message compact">{errors.personId.message}</p>}
                                        <input
                                            type="hidden"
                                            {...register('personId')}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.signees.form.company')}<span className="required">*</span></span>
                                        <select
                                            className="form-control"
                                            {...register('company')}
                                            disabled={isLoadingCompanies || !!editData || companies.length <= 1}
                                            value={watchedValues.company || ''}
                                        >
                                            {companies.length === 0 && (
                                                <option value="">{isLoadingCompanies ? t('common.loading') : t('common.select')}</option>
                                            )}
                                            {(companies || []).map((company) => (
                                                <option key={company.id} value={String(company.id)}>{getCompanyLabel(company)}</option>
                                            ))}
                                        </select>
                                        {errors.company && <p className="error-message compact">{errors.company.message}</p>}
                                    </div>
                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.signees.form.position')}<span className="required">*</span></span>
                                        <input
                                            maxLength={200}
                                            type="text"
                                            className="form-control"
                                            {...register('position')}
                                            value={watchedValues.position || ''}
                                        />
                                        {errors.position && <p className="error-message compact">{errors.position.message}</p>}
                                    </div>
                                    <div className="flex-row">
                                        <div className="checkbox-wrapper-start title-checkbox">
                                            <input
                                                type="checkbox"
                                                id="firstSignatures"
                                                {...register('firstSignatures')}
                                                checked={watchedValues.firstSignatures || false}
                                            />
                                            <span className="span-bold">{t('organizationalStructure.signees.form.firstSignatures')}</span>
                                        </div>
                                    </div>
                                    <div className="flex-row">
                                        <div className="checkbox-wrapper-start title-checkbox">
                                            <input
                                                type="checkbox"
                                                id="secondSignatures"
                                                {...register('secondSignatures')}
                                                checked={watchedValues.secondSignatures || false}
                                            />
                                            <span className="span-bold">{t('organizationalStructure.signees.form.secondSignatures')}</span>
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.jobDescriptions.form.status')}</span>
                                        <select className="form-control" {...register('status', { required: true })} value={watchedValues.status || '1'}>
                                            <option value="1">{t('organizationalStructure.jobDescriptions.form.active')}</option>
                                            <option value="2">{t('organizationalStructure.jobDescriptions.form.inactive')}</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <LoadingButton type="submit" className="btn btn-primary" loading={loading} disabled={loading}>{t('common.save')}</LoadingButton>
                                <button type="button" className="btn btn-secondary" onClick={handleClose} disabled={loading}>{t('common.cancel')}</button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    )
}